// src/components/Widgets/StaffCostWidget.jsx
import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import WidgetHeader from "../Common/WidgetHeader";
import StaffModal from "../Modals/StaffModal";
import { useStaff } from "../../contexts/StaffContext";
import { useFeedback } from "../../contexts/FeedbackContext";
import { navigateTo, ROUTES } from "../../lib/nav";

/* ---------- Helpers ---------- */
function coerceMonthlyHours(m) {
  const hm = Number(m?.hoursPerMonth);
  if (Number.isFinite(hm) && hm > 0) return hm;
  const hw = Number(m?.hoursPerWeek);
  return Number.isFinite(hw) && hw > 0 ? hw * 4.33 : 160;
}
function monthlyCostFor(member) {
  const sal = Number(member?.salary);
  if (Number.isFinite(sal) && sal > 0) return sal;
  const hr = Number(member?.hourlyRate);
  if (!Number.isFinite(hr) || hr <= 0) return 0;
  const hm = coerceMonthlyHours(member);
  return hr * hm;
}

export default function StaffCostWidget({
  staff = [],
  monthlyOutput = 1,
  currency = "AUD",
  backTo = "/dashboard",
}) {
  const navigate = useNavigate();
  const { addStaff, updateStaff } = useStaff();

  // Safe fallbacks so component never crashes if provider isn’t mounted
  const fb = (useFeedback && useFeedback()) || {};
  const play = fb.play || (() => {});
  const buzz = fb.buzz || (() => {});

  const fmt = useMemo(() => {
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency,
        currencyDisplay: "symbol",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    } catch {
      return { format: (n) => `A$${Number(n || 0).toFixed(2)}` };
    }
  }, [currency]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);

  const cleanStaff = useMemo(
    () => (Array.isArray(staff) ? staff.filter((m) => m?.isActive !== false) : []),
    [staff]
  );

  const { totalSalary, perUnit, headcount, byRole, sorted } = useMemo(() => {
    const totalSalary = cleanStaff.reduce((sum, m) => sum + monthlyCostFor(m), 0);
    const headcount = cleanStaff.length;
    const perUnit = monthlyOutput > 0 ? totalSalary / monthlyOutput : 0;

    const byRoleMap = cleanStaff.reduce((acc, m) => {
      const role = (m.role || "Team").trim();
      const cost = monthlyCostFor(m);
      const item = acc.get(role) || { role, cost: 0, count: 0 };
      item.cost += cost;
      item.count += 1;
      acc.set(role, item);
      return acc;
    }, new Map());

    const sorted = [...cleanStaff].sort((a, b) => monthlyCostFor(b) - monthlyCostFor(a));
    return {
      totalSalary,
      perUnit,
      headcount,
      byRole: Array.from(byRoleMap.values()).sort((a, b) => b.cost - a.cost),
      sorted,
    };
  }, [cleanStaff, monthlyOutput]);

  const openAdd = () => {
    setEditingStaff(null);
    setModalOpen(true);
  };
  const openEdit = (member) => {
    setEditingStaff(member);
    setModalOpen(true);
  };

  const handleSave = (data) => {
    try {
      if (editingStaff) {
        updateStaff?.({ ...editingStaff, ...data, id: editingStaff.id });
      } else {
        addStaff?.(data);
      }
      play("success");
      buzz("success");
    } catch {
      play("error");
      buzz("error");
    } finally {
      setModalOpen(false);
      setEditingStaff(null);
    }
  };

  const card = "rounded border p-3 bg-white/5 border-white/10";

  return (
    <div className="p-0 rounded-lg bg-transparent text-inherit space-y-3">
      <WidgetHeader title=" Staff Costs" to={backTo} />

      {/* KPI Row */}
      <div className="grid grid-cols-3 gap-2 text-sm tabular-nums">
        <div className={card}>
          <div className="text-[11px] opacity-70">Payroll (monthly)</div>
          <div className="text-xl font-semibold">{fmt.format(totalSalary)}</div>
        </div>
        <div className={card}>
          <div className="text-[11px] opacity-70">
            Cost / unit{monthlyOutput > 0 ? ` ×${monthlyOutput}` : ""}
          </div>
          <div className="text-xl font-semibold">
            {monthlyOutput > 0 ? fmt.format(perUnit) : "—"}
          </div>
        </div>
        <div className={card}>
          <div className="text-[11px] opacity-70">Headcount (active)</div>
          <div className="text-xl font-semibold">{headcount}</div>
        </div>
      </div>

      {/* Staff List */}
      <div className={card}>
        <div className="flex items-center justify-between mb-2">
          <div className="font-medium text-sm">Team</div>
          <div className="flex gap-2">
            <button
              onClick={openAdd}
              className="text-xs px-2 py-1 rounded bg-emerald-500 text-black hover:opacity-90"
            >
              + Add
            </button>
            {cleanStaff.length > 0 ? (
              <button
                onClick={() => navigateTo(navigate, ROUTES.STAFF, { source: 'StaffCostWidget:manage' })}
                className="text-xs underline opacity-70 hover:opacity-100"
              >
                Manage
              </button>
            ) : null}
          </div>
        </div>

        {cleanStaff.length === 0 ? (
          <div className="text-xs opacity-60">No active staff members yet.</div>
        ) : (
          <ul className="space-y-2 text-sm tabular-nums">
            {sorted.map((m, i) => {
              const cost = monthlyCostFor(m);
              const hasHourly = Number(m?.hourlyRate) > 0;
              const monthlyHours = hasHourly ? coerceMonthlyHours(m) : null;

              return (
                <li
                  key={`${m.id || i}`}
                  className="flex items-center justify-between gap-3 rounded border border-white/10 hover:border-white/20 transition p-2"
                >
                  <button
                    onClick={() => openEdit(m)}
                    className="flex-1 text-left min-w-0"
                    title="Edit staff"
                  >
                    <div className="truncate">
                      <span className="font-medium">{m.name || "Unnamed"}</span>{" "}
                      <span className="opacity-60">({m.role || "Team"})</span>
                      {hasHourly ? (
                        <span className="ml-2 text-[11px] opacity-60">
                          {fmt.format(m.hourlyRate)}/h · {Math.round(monthlyHours)}h/mo
                        </span>
                      ) : m?.salary ? (
                        <span className="ml-2 text-[11px] opacity-60">salary</span>
                      ) : null}
                    </div>
                  </button>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="font-medium">{fmt.format(cost)}</span>
                    <button
                      onClick={() => openEdit(m)}
                      className="text-[11px] underline opacity-70 hover:opacity-100"
                      title="Edit"
                    >
                      edit
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* By-role breakdown */}
      {byRole.length > 0 && (
        <div className={`${card} tabular-nums`}>
          <div className="font-medium text-sm mb-2">By role</div>
          <ul className="space-y-1 text-xs">
            {byRole.map((r) => (
              <li key={r.role} className="flex justify-between">
                <span>
                  {r.role} <span className="opacity-60">({r.count})</span>
                </span>
                <span className="font-medium">{fmt.format(r.cost)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Inline Staff Modal */}
      {modalOpen && (
        <StaffModal
          initialStaff={editingStaff}
          onClose={() => {
            setModalOpen(false);
            setEditingStaff(null);
          }}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

StaffCostWidget.propTypes = {
  staff: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      role: PropTypes.string,
      salary: PropTypes.number,
      hourlyRate: PropTypes.number,
      hoursPerWeek: PropTypes.number,
      hoursPerMonth: PropTypes.number,
      isActive: PropTypes.bool,
    })
  ),
  monthlyOutput: PropTypes.number,
  currency: PropTypes.string,
  backTo: PropTypes.string,
};
